import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { authApi } from "@/services/api/auth"; // Debe exponer getUserProfile(), updateAvatar(), sendVerificationEmail()
import { useAuth } from "@/store/authStore";
import { UserData } from "@/services/types/domain";
import { UPLOADS_URL } from "@/utils/utils";

const SectionCard: React.FC<{ title: string; right?: React.ReactNode; children: React.ReactNode }>
    = ({ title, right, children }) => (
        <section className="rounded-2xl border bg-white shadow-sm">
            <header className="flex items-center justify-between border-b px-5 py-4">
                <h2 className="text-base font-semibold text-gray-900">{title}</h2>
                {right}
            </header>
            <div className="p-5">{children}</div>
        </section>
    );

const Avatar: React.FC<{ src?: string | null; initials?: string }>
    = ({ src, initials }) => (
        <div className="h-16 w-16 overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200">
            {src ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={src} alt="avatar" className="h-full w-full object-cover" />
            ) : (
                <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-gray-600">
                    {initials ?? "👤"}
                </div>
            )}
        </div>
    );

const ProfilePage: React.FC = () => {
    const auth = (useAuth as any)?.();
    const currentUser: UserData | undefined = auth?.user as UserData | undefined;

    // Estado principal
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<UserData | null>(currentUser ?? null);
    const [refreshing, setRefreshing] = useState(false);

    // Tabs
    const [tab, setTab] = useState<"profile" | "security" | "account">("profile");

    // Form profile
    const [form, setForm] = useState<{ first_name: string; last_name: string; username: string; phone: string }>(
        {
            first_name: currentUser?.first_name ?? "",
            last_name: currentUser?.last_name ?? "",
            username: currentUser?.username ?? "",
            phone: currentUser?.phone ?? "",
        }
    );

    // Seguridad
    const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });

    // Cuenta
    const [newEmail, setNewEmail] = useState("");
    const [confirmPass, setConfirmPass] = useState("");

    // Busy flags
    const [savingProfile, setSavingProfile] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [changingEmail, setChangingEmail] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [sendingVerification, setSendingVerification] = useState(false);

    // Avatar upload
    const fileRef = useRef<HTMLInputElement | null>(null);

    const initials = useMemo(() => {
        const fn = (user?.first_name || "").trim();
        const ln = (user?.last_name || "").trim();
        const a = `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase();
        return a || (user?.email?.[0]?.toUpperCase() ?? "U");
    }, [user]);

    // ============ Fetch helpers ============
    const fillFromUser = (u: UserData) => {
        setUser(u);
        setForm({
            first_name: u.first_name ?? "",
            last_name: u.last_name ?? "",
            username: u.username ?? "",
            phone: u.phone ?? "",
        });
        auth?.setUser?.(u);
    };

    const fetchProfile = async (silent = false) => {
        try {
            if (!silent) setLoading(true); else setRefreshing(true);
            // GET 'user/profile' → ApiResponse<UserData>
            const fresh: UserData = await (authApi as any).getUserProfile();
            fillFromUser(fresh);
        } catch (e: any) {
            console.error(e);
            if (!user) toast.error(e?.response?.data?.message || e?.message || "No se pudo cargar el perfil");
        } finally {
            if (!silent) setLoading(false); else setRefreshing(false);
        }
    };

    // Carga inicial
    useEffect(() => {
        fetchProfile(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ============ Actions ============
    const handleProfileSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSavingProfile(true);
            const fn = (authApi as any).updateProfile || (authApi as any).updateUser;
            if (!fn) throw new Error("Método updateProfile no disponible en authApi");
            await fn({
                first_name: form.first_name,
                last_name: form.last_name,
                username: form.username,
                phone: form.phone,
            });
            toast.success("Perfil actualizado");
            await fetchProfile(true);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo actualizar el perfil");
        } finally {
            setSavingProfile(false);
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!pwd.next || pwd.next !== pwd.confirm) {
            toast.error("Las contraseñas no coinciden");
            return;
        }
        try {
            setChangingPassword(true);
            const fn = (authApi as any).changePassword || (authApi as any).updatePassword;
            if (!fn) throw new Error("Método de cambio de contraseña no disponible");
            await fn({ current_password: pwd.current, new_password: pwd.next, confirm_password: pwd.confirm });
            setPwd({ current: "", next: "", confirm: "" });
            toast.success("Contraseña actualizada");
            await fetchProfile(true);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo actualizar la contraseña");
        } finally {
            setChangingPassword(false);
        }
    };

    const handleEmailChange = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newEmail || !confirmPass) {
            toast.error("Completa email y contraseña actual");
            return;
        }
        try {
            setChangingEmail(true);
            const emailFn = (authApi as any).changeEmail || (authApi as any).updateEmail;
            if (!emailFn) throw new Error("Método para actualizar email no disponible");
            await emailFn({ email: newEmail, password: confirmPass });
            toast.success("Email actualizado / pendiente de verificación");
            setNewEmail("");
            setConfirmPass("");
            await fetchProfile(true);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo actualizar el email");
        } finally {
            setChangingEmail(false);
        }
    };

    const handleSendVerificationEmail = async () => {
        try {
            setSendingVerification(true);
            await (authApi as any).sendVerificationEmail(); // POST '/auth/send-verification-email'
            toast.success("Te enviamos un correo con el enlace de verificación");
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo enviar la verificación");
        } finally {
            setSendingVerification(false);
        }
    };

    const handleAvatarUpload = async (file: File) => {
        try {
            setAvatarUploading(true);
            const fn = (authApi as any).updateAvatar || (authApi as any).uploadAvatar;
            if (!fn) throw new Error("Método de avatar no disponible");
            await fn(file); // POST multipart
            toast.success("Avatar actualizado");
            await fetchProfile(true);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo actualizar el avatar");
        } finally {
            if (fileRef.current) fileRef.current.value = "";
            setAvatarUploading(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (!confirmPass) {
            toast.error("Confirma tu contraseña");
            return;
        }
        try {
            setDeleting(true);
            await (authApi as any).deleteAccount(confirmPass);
            toast.success("Cuenta eliminada");
            (auth as any)?.logout?.();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo eliminar la cuenta");
        } finally {
            setDeleting(false);
        }
    };

    if (loading && !user) {
        return (
            <div className="min-h-screen grid place-items-center bg-gray-50">
                <div className="animate-pulse text-gray-500">Cargando perfil…</div>
            </div>
        );
    }

    const isVerified = Boolean(user?.email_verified_at);
    const lastLogin = user?.last_login_at ? new Date(user.last_login_at).toLocaleString() : "—";
    const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—";

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-8">
            <div className="mx-auto w-full max-w-6xl">
                {/* Header */}
                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-4">
                        <Avatar src={UPLOADS_URL + (user?.profile_image ?? '')} initials={initials} />
                        <div>
                            <div className="text-xl font-bold text-gray-900">{user?.first_name} {user?.last_name}</div>
                            <div className="text-gray-600">{user?.email}</div>
                            <div className="text-xs text-gray-500">Miembro desde {memberSince}</div>
                            {user?.seller_info?.store_name && (
                                <div className="text-xs text-gray-500">Tienda: {user.seller_info.store_name}</div>
                            )}
                        </div>
                        {refreshing && <span className="ml-2 text-xs text-gray-500">Actualizando…</span>}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleAvatarUpload(f);
                            }}
                        />
                        <Button onClick={() => fileRef.current?.click()} disabled={avatarUploading}>
                            {avatarUploading ? "Subiendo…" : "Cambiar avatar"}
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-4 flex flex-wrap gap-2">
                    {([
                        { key: "profile", label: "Perfil" },
                        { key: "security", label: "Seguridad" },
                        { key: "account", label: "Cuenta" },
                    ] as const).map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)} className="focus:outline-none">
                            <Badge variant={tab === t.key ? "secondary" : "outline"}>{t.label}</Badge>
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {/* Columna principal */}
                    <div className="space-y-6 lg:col-span-2">
                        {tab === "profile" && (
                            <SectionCard title="Información personal">
                                <form onSubmit={handleProfileSave} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="sm:col-span-1">
                                        <label htmlFor="first_name" className="mb-1 block text-sm font-medium text-gray-700">Nombres</label>
                                        <Input id="first_name" value={form.first_name} onChange={(e) => setForm(s => ({ ...s, first_name: e.target.value }))} placeholder="Ej. Juan" />
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label htmlFor="last_name" className="mb-1 block text-sm font-medium text-gray-700">Apellidos</label>
                                        <Input id="last_name" value={form.last_name} onChange={(e) => setForm(s => ({ ...s, last_name: e.target.value }))} placeholder="Ej. Pérez" />
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label htmlFor="username" className="mb-1 block text-sm font-medium text-gray-700">Usuario</label>
                                        <Input id="username" value={form.username} onChange={(e) => setForm(s => ({ ...s, username: e.target.value }))} placeholder="miusuario" />
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label htmlFor="phone" className="mb-1 block text-sm font-medium text-gray-700">Teléfono</label>
                                        <Input id="phone" value={form.phone} onChange={(e) => setForm(s => ({ ...s, phone: e.target.value }))} placeholder="+51 9xx xxx xxx" />
                                    </div>

                                    <div className="sm:col-span-2 border-t pt-4">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">Correo</div>
                                                <div className="text-sm text-gray-600">{user?.email}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant={isVerified ? "success" : "outline"}>{isVerified ? "Verificado" : "No verificado"}</Badge>
                                                {!isVerified && (
                                                    <Button type="button" onClick={handleSendVerificationEmail} disabled={sendingVerification}>
                                                        {sendingVerification ? "Enviando…" : "Enviar verificación"}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:col-span-2">
                                        <Button type="submit" disabled={savingProfile}>{savingProfile ? "Guardando…" : "Guardar cambios"}</Button>
                                    </div>
                                </form>
                            </SectionCard>
                        )}

                        {tab === "security" && (
                            <SectionCard title="Cambiar contraseña">
                                <form onSubmit={handlePasswordChange} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="sm:col-span-2">
                                        <label htmlFor="current" className="mb-1 block text-sm font-medium text-gray-700">Contraseña actual</label>
                                        <Input id="current" type="password" value={pwd.current} onChange={(e) => setPwd(s => ({ ...s, current: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label htmlFor="next" className="mb-1 block text-sm font-medium text-gray-700">Nueva contraseña</label>
                                        <Input id="next" type="password" value={pwd.next} onChange={(e) => setPwd(s => ({ ...s, next: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label htmlFor="confirm" className="mb-1 block text-sm font-medium text-gray-700">Confirmar contraseña</label>
                                        <Input id="confirm" type="password" value={pwd.confirm} onChange={(e) => setPwd(s => ({ ...s, confirm: e.target.value }))} />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Button type="submit" disabled={changingPassword}>{changingPassword ? "Actualizando…" : "Actualizar contraseña"}</Button>
                                    </div>
                                </form>
                            </SectionCard>
                        )}

                        {tab === "account" && (
                            <>
                                <SectionCard title="Cambiar correo">
                                    <form onSubmit={handleEmailChange} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div className="sm:col-span-2">
                                            <label className="mb-1 block text-sm font-medium text-gray-700">Nuevo correo</label>
                                            <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="nuevo@correo.com" />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="mb-1 block text-sm font-medium text-gray-700">Confirmar con tu contraseña</label>
                                            <Input type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Button type="submit" disabled={changingEmail}>{changingEmail ? "Actualizando…" : "Actualizar correo"}</Button>
                                        </div>
                                    </form>
                                </SectionCard>

                                <SectionCard title="Eliminar cuenta" right={<Badge variant="destructive">Acción irreversible</Badge>}>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div className="sm:col-span-2 text-sm text-gray-600">
                                            Esta acción eliminará tu cuenta y datos asociados. Escribe tu contraseña para confirmar.
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Input type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} placeholder="Contraseña actual" />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Button type="button" onClick={handleDeleteAccount} disabled={deleting} className="bg-red-600 hover:bg-red-700">
                                                {deleting ? "Eliminando…" : "Eliminar cuenta"}
                                            </Button>
                                        </div>
                                    </div>
                                </SectionCard>
                            </>
                        )}
                    </div>

                    {/* Columna lateral */}
                    <aside className="space-y-6">
                        <section className="rounded-2xl border bg-white p-5 shadow-sm">
                            <h3 className="mb-3 text-base font-semibold text-gray-900">Estado de la cuenta</h3>
                            <div className="space-y-2 text-sm text-gray-700">
                                <div className="flex items-center justify-between">
                                    <span>Correo verificado</span>
                                    <Badge variant={isVerified ? "success" : "outline"}>{isVerified ? "Sí" : "No"}</Badge>
                                </div>
                                {!isVerified && (
                                    <div className="flex items-center justify-between">
                                        <span>Acción</span>
                                        <Button type="button" onClick={handleSendVerificationEmail} disabled={sendingVerification} className="px-3 py-1 text-sm">
                                            {sendingVerification ? "Enviando…" : "Enviar verificación"}
                                        </Button>
                                    </div>
                                )}
                                <div className="flex items-center justify-between">
                                    <span>Tipo de usuario</span>
                                    <Badge variant="outline">{user?.user_type ?? "—"}</Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span>Estado</span>
                                    <Badge variant={user?.status === "active" ? "success" : user?.status === "banned" ? "destructive" : "outline"}>
                                        {user?.status ?? "—"}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span>Último acceso</span>
                                    <span className="font-medium">{lastLogin}</span>
                                </div>
                            </div>
                        </section>

                        <section className="rounded-2xl border bg-white p-5 shadow-sm">
                            <h3 className="mb-3 text-base font-semibold text-gray-900">Resumen</h3>
                            <div className="space-y-2 text-sm text-gray-700">
                                <div className="flex items-center justify-between">
                                    <span>Pedidos totales</span>
                                    <span className="font-medium">{user?.total_orders ?? 0}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span>Total gastado</span>
                                    <span className="font-medium">S/ {(user?.total_spent ?? 0).toFixed(2)}</span>
                                </div>
                                {user?.seller_info?.verification_status && (
                                    <div className="flex items-center justify-between">
                                        <span>Verificación tienda</span>
                                        <Badge variant="outline">{user.seller_info.verification_status}</Badge>
                                    </div>
                                )}
                            </div>
                        </section>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;


